import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";
import { Send, CheckCircle2, MapPin, ArrowLeft } from "lucide-react";
import { IndonesiaMap } from "@/components/IndonesiaMap";
import { useLang, LangToggle } from "@/lib/i18n";

export default function PublicRequest() {
  const [company, setCompany] = useState({ name: "GRCom", tagline: "IT Support & Infrastruktur", phone: "", email: "", website: "" });
  const [form, setForm] = useState({ customer_name: "", customer_company: "", customer_email: "", customer_phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const { t } = useLang();

  useEffect(() => { api.get("/public/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.customer_name.trim() || !form.message.trim()) { setError(t("Nama dan detail kebutuhan wajib diisi.")); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/quote-request", form);
      setDone(data.reference);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-background grid-noise">
      <header className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center gap-3">
          <Link to="/" className="bg-white rounded-sm px-3 py-2 inline-flex" data-testid="request-logo-home">
            <img src="/grcom-logo.png" alt="CV GRcom" className="h-8 w-auto" />
          </Link>
          <div className="overline text-muted-foreground hidden sm:block">{t(company.tagline)}</div>
          <div className="ml-auto"><LangToggle /></div>
          <Link to="/" data-testid="request-back-home" className="inline-flex items-center gap-2 text-sm font-medium border border-border rounded-sm px-4 py-2 hover:bg-secondary transition-colors">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("Kembali ke Beranda")}</span>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="overline text-accent mb-3">{t("Permintaan Penawaran Harga")}</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mb-3" data-testid="request-title">{t("Minta Penawaran Barang & Jasa IT")}</h1>
        <p className="text-muted-foreground mb-8 max-w-xl">
          {t("Sampaikan kebutuhan Anda (laptop, server, networking, jasa IT). Tim kami akan menyusun penawaran harga terbaik dan menghubungi Anda.")}
        </p>

        <section className="border border-border bg-card rounded-sm overflow-hidden mb-10 fade-up" data-testid="coverage-section">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 flex flex-col justify-center">
              <div className="overline text-accent mb-3">{t("Jangkauan Nasional")}</div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-3">
                {t("Tim CV GRCom Tersebar di Seluruh Indonesia")}
              </h2>
              <p className="text-muted-foreground text-sm mb-5 max-w-md">
                {t("Tim kami siap mendukung infrastruktur IT perusahaan Anda di mana pun berada.")}
              </p>
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent/50" />
                  <MapPin className="w-3.5 h-3.5 text-accent" />
                </span>
                {t("Kantor Pusat")} : <span className="text-accent font-semibold">{t("Medan, Sumatera Utara")}</span>
              </div>
            </div>
            <div className="bg-white flex items-center justify-center p-4">
              <IndonesiaMap />
            </div>
          </div>
        </section>

        {done ? (
          <div className="border border-emerald-500/40 bg-emerald-500/5 rounded-sm p-8 text-center fade-up" data-testid="request-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-xl mb-2">{t("Permintaan Terkirim!")}</h2>
            <p className="text-muted-foreground text-sm">{t("Nomor referensi Anda:")} <span className="font-mono text-foreground">{done}</span></p>
            <p className="text-muted-foreground text-sm mt-2">{t("Tim")} {company.name} {t("akan segera memproses dan menghubungi Anda.")}</p>
            <Button className="mt-6 rounded-sm" onClick={() => { setDone(null); setForm({ customer_name: "", customer_company: "", customer_email: "", customer_phone: "", message: "" }); }}>
              {t("Kirim Permintaan Lain")}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-border bg-card rounded-sm p-6 sm:p-8 space-y-4 fade-up" data-testid="public-request-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="overline text-muted-foreground">{t("Nama Anda *")}</Label>
                <Input data-testid="pr-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="mt-1 rounded-sm bg-secondary" required /></div>
              <div><Label className="overline text-muted-foreground">{t("Perusahaan")}</Label>
                <Input data-testid="pr-company" value={form.customer_company} onChange={(e) => setForm({ ...form, customer_company: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Email</Label>
                <Input data-testid="pr-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">{t("Telepon / WhatsApp")}</Label>
                <Input data-testid="pr-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            </div>
            <div><Label className="overline text-muted-foreground">{t("Detail Kebutuhan *")}</Label>
              <Textarea data-testid="pr-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("Contoh: 10 unit laptop Lenovo ThinkPad E14 Gen 5 (16GB/512GB) untuk kantor cabang.")}
                className="mt-1 rounded-sm bg-secondary min-h-[120px]" required /></div>
            {error && <div className="text-sm text-destructive font-mono" data-testid="pr-error">{error}</div>}
            <Button data-testid="pr-submit" type="submit" disabled={sending} className="rounded-sm">
              <Send className="w-4 h-4 mr-2" /> {sending ? t("Mengirim…") : t("Kirim Permintaan")}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
